import { Glyph, HorizonS, ParcelMark, SectionMark, WordSet, wordWidth } from "./marks";

const CAP = 625;

// ---------- Refined candidates ----------
// R1 · HORIZON — gap tuned to the stem weight, shift = one stroke width; SS compact pairs two cut initials
export const R_Horizon = {
  key: "horizon",
  name: "Horizon",
  symbolViewBox: "-40 -690 600 740",
  Symbol: () => <HorizonS wght={600} gap={22} shift={38} />,
  renderS: (l, w) => <HorizonS wght={w} gap={16} shift={24} />,
  lockupScale: 1.1,
  Compact: () => (
    <>
      <HorizonS wght={600} gap={22} shift={38} />
      <g transform="translate(470 0)"><HorizonS wght={600} gap={22} shift={38} /></g>
    </>
  ),
  compactViewBox: "-40 -690 1060 740",
  rationale: "Gap set to 22 units (≈ the thin stroke); displacement 38 units (≈ one stem). Below 20px the shift is still legible as a step — the cut survives at favicon size because it is a horizontal void, which rasterises cleanly.",
};

// R2 · SECTION — true interlock: lower S passes over at the upper crossing, under at the lower one
export const SectionRefined = ({ bg = "#F7F5F0", wght = 600 }) => {
  const dy = CAP * 0.48;
  return (
    <>
      <Glyph ch="S" wght={wght} />
      <g transform={`translate(0 ${dy})`}>
        <Glyph ch="S" wght={wght} fill="none" stroke={bg} strokeWidth={26} strokeLinejoin="round" />
        <Glyph ch="S" wght={wght} />
      </g>
      {/* upper S re-asserted over the lower crossing */}
      <clipPath id="sec-ref-clip"><rect x="-100" y={-CAP / 2 + 40} width="800" height="300" /></clipPath>
      <g clipPath="url(#sec-ref-clip)">
        <Glyph ch="S" wght={wght} fill="none" stroke={bg} strokeWidth={26} strokeLinejoin="round" />
        <Glyph ch="S" wght={wght} />
      </g>
    </>
  );
};
export const R_Section = {
  key: "section",
  name: "Section",
  symbolViewBox: "-40 -690 580 1050",
  Symbol: ({ bg }) => <SectionRefined bg={bg} />,
  renderS: null,
  lockupScale: 0.74,
  Compact: ({ bg }) => <SectionRefined bg={bg} />,
  compactViewBox: "-40 -690 580 1050",
  rationale: "Overlap reduced to 48% of cap height so each S keeps its own counters. The interlock is now a true over/under weave — reads as a single reference sign at 16px, and as two S forms above 32px.",
};

// R3 · PARCEL — frame proportion re-cut to the S's optical square; open edge sits exactly on the baseline
export const ParcelRefined = ({ wght = 600, sw = 20 }) => {
  const x0 = -120, y0 = -720, size = 740;
  return (
    <>
      <path d={`M ${x0} ${y0 + size} V ${y0} H ${x0 + size} V ${y0 + size}`} fill="none" stroke="currentColor" strokeWidth={sw} />
      <Glyph ch="S" wght={wght} x={10} y={-8} />
    </>
  );
};
export const R_Parcel = {
  key: "parcel",
  name: "Parcel",
  symbolViewBox: "-180 -780 860 820",
  Symbol: () => <ParcelRefined />,
  renderS: null,
  lockupScale: 0.86,
  Compact: () => <ParcelRefined />,
  compactViewBox: "-180 -780 860 820",
  rationale: "Frame stroke equalised to the S hairline; open lower edge aligned to the S baseline so the letter stands on the implied horizon. Frame and letter now share one optical weight.",
};

export const REFINED = [R_Horizon, R_Section, R_Parcel];

// ---------- helpers for application mock-ups ----------
export const SymbolSVG = ({ c, size, bg, style, className }) => (
  <svg viewBox={c.symbolViewBox} width={size} height={size} preserveAspectRatio="xMidYMid meet" style={{ display: "block", ...style }} className={className}>
    <c.Symbol bg={bg} />
  </svg>
);

export const Word = ({ c, height = 14, tracking = 70, style }) => {
  const w = wordWidth("SELLING SUNSETS", { tracking });
  return (
    <svg viewBox={`0 -${CAP + 20} ${w} ${CAP + 40}`} height={height} width={(height * w) / (CAP + 40)} style={{ display: "block", ...style }} role="img" aria-label="Selling Sunsets">
      <WordSet tracking={tracking} renderS={c.renderS} />
    </svg>
  );
};

export const LockupRow = ({ c, height = 14, bg, gap = 0.28, style }) => (
  <div style={{ display: "flex", alignItems: "center", gap: `${height * gap}px`, ...style }}>
    <SymbolSVG c={c} size={height * (c.lockupScale * 1.25)} bg={bg} />
    <Word c={c} height={height} />
  </div>
);
